import { useState, useCallback, useRef, useEffect } from 'preact/hooks';
import type {IColumnWidths, IUseColumnResize} from "../types";

export const useColumnResize = (
    initialWidths: IColumnWidths,
    saveKey?: string
): IUseColumnResize => {
    const [columnWidths, setColumnWidths] = useState<IColumnWidths>(() => {
        if (saveKey) {
            const saved = localStorage.getItem(saveKey);
            return saved ? JSON.parse(saved) : initialWidths;
        }
        return initialWidths;
    });

    const currentColumn = useRef<string | null>(null);
    const startX = useRef<number>(0);
    const startWidth = useRef<number>(0);

    useEffect(() => {
        if (saveKey) {
            localStorage.setItem(saveKey, JSON.stringify(columnWidths));
        }
    }, [columnWidths, saveKey]);

    const handleMouseDown = useCallback((columnKey: string) => (e: MouseEvent) => {
        e.stopPropagation();
        currentColumn.current = columnKey;
        startX.current = e.pageX;
        startWidth.current = columnWidths[columnKey] || 100;

        const handleMouseMove = (moveEvent: MouseEvent) => {
            if (!currentColumn.current) return;
            const diff = moveEvent.pageX - startX.current;
            const newWidth = Math.max(50, startWidth.current + diff);
            setColumnWidths(prev => ({
                ...prev,
                [currentColumn.current!]: newWidth
            }));
        };

        const handleMouseUp = () => {
            currentColumn.current = null;
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, [columnWidths]);

    const resizeHandleProps = useCallback((columnKey: string) => ({
        onMouseDown: handleMouseDown(columnKey)
    }), [handleMouseDown]);

    const headerProps = useCallback((columnKey: string) => ({
        style: {
            minWidth: `${columnWidths[columnKey] || 100}px`,
            width: `${columnWidths[columnKey] || 100}px`
        }
    }), [columnWidths]);

    return {
        columnWidths,
        resizeHandleProps,
        headerProps
    };
};